import { Download, FileX2, RefreshCw, RotateCcw, Search, Trash2, Upload } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { apiDelete, apiGet, apiPost } from "@/lib/client/api";

type FileRow = {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  purpose: string;
  uploadedByName: string | null;
  createdAt: number;
  deletedAt: number | null;
  deletionStartedAt: number | null;
  referenceCount: number;
};

type FilesResponse = { files: FileRow[] };
type BulkFileAction = "trash" | "restore" | "permanent" | "force_permanent";
type DeletionPreview = { id: string; fileName: string; referenceVersion: number; references: Array<{ kind: string; recordId: string; label: string; active: boolean }> };
type BulkFileResponse = {
  succeeded: string[];
  skipped: Array<{ id: string; reason: string; blockers?: string[] }>;
  failed?: Array<{ id: string; reason: string }>;
};

const purposes = ["", "attachment", "company_logo", "client_logo", "font", "pdf_export", "excel_export"];

export function FilesSettings() {
  const { language } = useAppSettings();
  const text = language === "zh" ? zh : en;
  const [files, setFiles] = useState<FileRow[]>([]);
  const [trash, setTrash] = useState(false);
  const [query, setQuery] = useState("");
  const [purpose, setPurpose] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<BulkFileAction | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forcePreview, setForcePreview] = useState<DeletionPreview[] | null>(null);
  const [forceConfirmed, setForceConfirmed] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const busy = actionId !== null || bulkAction !== null || forcePreview !== null;

  useEffect(() => {
    if (forcePreview) { setForceConfirmed(false); dialog.current?.showModal(); }
    else dialog.current?.close();
  }, [forcePreview]);

  async function requestForceDeletion(ids: string[]) {
    setBulkAction("force_permanent");
    setError(null);
    try {
      const previews: DeletionPreview[] = [];
      for (let index = 0; index < ids.length; index += 10) {
        const response = await apiPost<{ ids: string[] }, { files: DeletionPreview[] }>("/api/files/deletion-preview", { ids: ids.slice(index, index + 10) });
        previews.push(...response.files);
      }
      if (previews.length) setForcePreview(previews);
      else await loadFiles();
    } catch (cause) { setError(cause instanceof Error ? cause.message : text.actionError); }
    finally { setBulkAction(null); }
  }

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ trash: trash ? "1" : "0" });
      if (query.trim()) params.set("q", query.trim());
      if (purpose) params.set("purpose", purpose);
      const response = await apiGet<FilesResponse>(`/api/files?${params}`);
      setFiles(response.files);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : text.loadError);
    } finally {
      setLoading(false);
    }
  }, [purpose, query, text.loadError, trash]);

  useEffect(() => {
    void loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    const visibleIds = new Set(files.map((file) => file.id));
    setSelectedIds((current) => new Set([...current].filter((id) => visibleIds.has(id))));
  }, [files]);

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setMessage(null);
    try {
      const form = new FormData();
      form.set("file", file);
      form.set("purpose", "attachment");
      const response = await fetch("/api/files/upload", { method: "POST", credentials: "include", body: form });
      const payload = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(payload.error ?? text.uploadError);
      setMessage(text.uploaded);
      await loadFiles();
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : text.uploadError);
    } finally {
      event.target.value = "";
    }
  }

  async function mutate(file: FileRow, action: "trash" | "restore" | "permanent") {
    if (action === "permanent" && !window.confirm(text.confirmPermanent)) return;
    if (action === "trash" && !window.confirm(text.confirmTrash)) return;

    setActionId(file.id);
    setError(null);
    setMessage(null);
    try {
      if (action === "trash") await apiDelete(`/api/files/${file.id}`);
      if (action === "restore") await apiPost(`/api/files/${file.id}/restore`, {});
      if (action === "permanent") await apiDelete(`/api/files/${file.id}/permanent`);
      setMessage(action === "restore" ? text.restored : text.deleted);
      await loadFiles();
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : text.actionError);
    } finally {
      setActionId(null);
    }
  }

  async function mutateSelected(action: BulkFileAction, ids = [...selectedIds], referenceVersions?: Record<string, number>) {
    if (ids.length === 0) return;
    if (action === "trash" && !window.confirm(text.confirmBulkTrash.replace("{count}", String(selectedIds.size)))) return;
    if (action === "permanent" && !window.confirm(text.confirmBulkPermanent.replace("{count}", String(selectedIds.size)))) return;

    setBulkAction(action);
    setError(null);
    setMessage(null);
    try {
      const response: BulkFileResponse = { succeeded: [], skipped: [], failed: [] };
      // Small batches also stay below the per-request database limits on free Workers.
      for (let index = 0; index < ids.length; index += 2) {
        const part = await apiPost<{ ids: string[]; action: BulkFileAction; referenceVersions?: Record<string, number> }, BulkFileResponse>("/api/files/bulk", { ids: ids.slice(index, index + 2), action, referenceVersions });
        response.succeeded.push(...part.succeeded);
        response.skipped.push(...part.skipped);
        response.failed!.push(...(part.failed ?? []));
      }
      setSelectedIds(new Set([...response.skipped, ...(response.failed ?? [])].map((item) => item.id)));
      setMessage(
        text.bulkSummary
          .replace("{completed}", String(response.succeeded.length))
          .replace("{skipped}", String(response.skipped.length)),
      );
      await loadFiles();
      if (response.failed?.length) setError(text.failed.replace("{count}", String(response.failed.length)));
      if (response.skipped.some((item) => item.reason === "stale_confirmation")) setError(text.stale);
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : text.actionError);
    } finally {
      setBulkAction(null);
      setForcePreview(null);
    }
  }

  const allVisibleSelected = files.length > 0 && files.every((file) => selectedIds.has(file.id));

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{text.title}</h1>
          <p className="text-sm text-muted-foreground">{text.description}</p>
        </div>
        <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          <Upload className="h-4 w-4" aria-hidden="true" />
          {text.upload}
          <input type="file" className="hidden" disabled={busy} onChange={upload} />
        </label>
      </header>

      {message || error ? <div className={error ? "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" : "rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"}>{error ?? message}</div> : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant={trash ? "secondary" : "primary"} onClick={() => setTrash(false)}>{text.active}</Button>
        <Button type="button" variant={trash ? "primary" : "secondary"} onClick={() => setTrash(true)}>{text.trash}</Button>
        <div className="relative min-w-52 flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input className="pl-9" value={query} placeholder={text.search} onChange={(event) => setQuery(event.target.value)} />
        </div>
        <select className="h-9 rounded-md border border-border bg-background px-3 text-sm" value={purpose} onChange={(event) => setPurpose(event.target.value)}>
          {purposes.map((value) => <option key={value || "all"} value={value}>{value ? text.purpose[value as keyof typeof text.purpose] : text.allPurposes}</option>)}
        </select>
        <Button type="button" variant="secondary" onClick={() => void loadFiles()} disabled={loading} title={text.refresh}>
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {selectedIds.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 border-y border-border py-2">
          <span className="text-sm font-medium">{text.selected.replace("{count}", String(selectedIds.size))}</span>
          <Button type="button" variant="secondary" className="text-destructive" disabled={busy} onClick={() => void requestForceDeletion([...selectedIds])}><FileX2 className="h-4 w-4" aria-hidden="true" />{text.force}</Button>
          {!trash ? <Button type="button" variant="secondary" className="text-destructive" disabled={busy} onClick={() => void mutateSelected("trash")}><Trash2 className="h-4 w-4" aria-hidden="true" />{text.bulkTrash}</Button> : null}
          {trash ? <Button type="button" variant="secondary" disabled={busy} onClick={() => void mutateSelected("restore")}><RotateCcw className="h-4 w-4" aria-hidden="true" />{text.bulkRestore}</Button> : null}
          {trash ? <Button type="button" variant="secondary" className="text-destructive" disabled={busy} onClick={() => void mutateSelected("permanent")}><Trash2 className="h-4 w-4" aria-hidden="true" />{text.bulkPermanent}</Button> : null}
        </div>
      ) : null}

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[840px] text-left text-sm">
          <thead className="border-b border-border bg-muted/60 text-xs text-muted-foreground">
            <tr><th className="w-12 px-4 py-3"><input type="checkbox" checked={allVisibleSelected} aria-label={text.selectAll} onChange={(event) => setSelectedIds(event.target.checked ? new Set(files.map((file) => file.id)) : new Set())} /></th><th className="px-4 py-3">{text.file}</th><th className="px-4 py-3">{text.purposeLabel}</th><th className="px-4 py-3">{text.size}</th><th className="px-4 py-3">{text.uploader}</th><th className="px-4 py-3">{text.created}</th><th className="px-4 py-3">{text.reference}</th><th className="px-4 py-3 text-right">{text.actions}</th></tr>
          </thead>
          <tbody>
            {files.map((file) => (
              <tr key={file.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3"><input type="checkbox" checked={selectedIds.has(file.id)} aria-label={`${text.select} ${file.fileName}`} onChange={(event) => setSelectedIds((current) => { const next = new Set(current); if (event.target.checked) next.add(file.id); else next.delete(file.id); return next; })} /></td>
                <td className="max-w-72 px-4 py-3"><div className="truncate font-medium">{file.fileName}</div><div className="truncate text-xs text-muted-foreground">{file.mimeType}</div></td>
                <td className="px-4 py-3">{text.purpose[file.purpose as keyof typeof text.purpose] ?? file.purpose}</td>
                <td className="px-4 py-3 tabular-nums">{formatBytes(file.sizeBytes)}</td>
                <td className="px-4 py-3">{file.uploadedByName ?? "-"}</td>
                <td className="px-4 py-3">{new Date(file.createdAt).toLocaleDateString()}</td>
                <td className="px-4 py-3">{file.deletionStartedAt ? text.pending : file.referenceCount > 0 ? text.inUse.replace("{count}", String(file.referenceCount)) : text.unused}</td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1">
                  <Button type="button" variant="secondary" className="h-8 w-8 shrink-0 p-0 text-destructive" onClick={() => void requestForceDeletion([file.id])} disabled={busy} title={file.deletionStartedAt ? text.retry : text.force} aria-label={file.deletionStartedAt ? text.retry : text.force}><FileX2 className="h-4 w-4" aria-hidden="true" /></Button>
                  {!trash ? <a className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-muted" href={`/api/files/${file.id}`} target="_blank" rel="noreferrer" title={text.download}><Download className="h-4 w-4" aria-hidden="true" /></a> : null}
                  {!trash ? <Button type="button" variant="secondary" className="h-8 w-8 p-0 text-destructive" onClick={() => void mutate(file, "trash")} disabled={busy || file.referenceCount > 0} title={file.referenceCount > 0 ? text.removeReferences : text.delete}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button> : null}
                  {trash ? <Button type="button" variant="secondary" className="h-8 w-8 p-0" onClick={() => void mutate(file, "restore")} disabled={busy || Boolean(file.deletionStartedAt)} title={text.restore}><RotateCcw className="h-4 w-4" aria-hidden="true" /></Button> : null}
                  {trash ? <Button type="button" variant="secondary" className="h-8 w-8 p-0 text-destructive" onClick={() => void mutate(file, "permanent")} disabled={busy || file.referenceCount > 0} title={file.referenceCount > 0 ? text.removeReferences : text.permanent}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button> : null}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading ? <div className="px-4 py-8 text-sm text-muted-foreground">{text.loading}</div> : null}
        {!loading && files.length === 0 ? <div className="px-4 py-8 text-sm text-muted-foreground">{text.empty}</div> : null}
      </Card>
      <dialog ref={dialog} onCancel={(event) => { if (bulkAction) event.preventDefault(); else setForcePreview(null); }} aria-labelledby="force-file-title" className="w-[min(640px,calc(100vw-32px))] rounded-md border border-border bg-background p-0 text-foreground backdrop:bg-black/40">
        <div className="space-y-4 p-5">
          <h2 id="force-file-title" className="text-lg font-semibold">{text.force}</h2>
          <p className="text-sm">{text.forceWarning}</p>
          <ul className="max-h-[50vh] space-y-3 overflow-y-auto border-y border-border py-3 text-sm">
            {forcePreview?.map((file) => <li key={file.id} className="break-words"><strong>{file.fileName}</strong><ul className="mt-1 space-y-1 text-xs text-muted-foreground">{file.references.map((ref, index) => <li key={`${ref.recordId}-${index}`}>{ref.label} · {ref.active ? text.activeUse : text.archival}</li>)}{!file.references.length ? <li>{text.unused}</li> : null}</ul></li>)}
          </ul>
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={forceConfirmed} disabled={bulkAction !== null} onChange={(event) => setForceConfirmed(event.target.checked)} />{text.acknowledge}</label>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" disabled={bulkAction !== null} onClick={() => setForcePreview(null)}>{text.cancel}</Button>
            <Button type="button" variant="secondary" className="text-destructive" disabled={!forceConfirmed || bulkAction !== null} onClick={() => void mutateSelected("force_permanent", forcePreview!.map((file) => file.id), Object.fromEntries(forcePreview!.map((file) => [file.id, file.referenceVersion])))}><FileX2 className="h-4 w-4" aria-hidden="true" />{text.force}</Button>
          </div>
        </div>
      </dialog>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const en = {
  force: "Force delete", forceWarning: "Permanently delete these files and unlink the records below. Quotes, companies and accounts will be kept. This cannot be undone.", acknowledge: "I understand that these files will be permanently removed and their references cleared.", cancel: "Cancel", activeUse: "Active reference", archival: "Historical reference", pending: "Deletion pending", retry: "Retry deletion", failed: "{count} files could not be deleted. Retry pending deletions in the recycle bin.", stale: "File references changed. Review the current references and confirm again.",
  title: "Attachments", description: "Manage uploaded assets and generated files. Files in use must be unlinked before permanent deletion.", upload: "Upload attachment", uploaded: "Attachment uploaded.", uploadError: "Unable to upload attachment.", active: "Files", trash: "Recycle bin", search: "Search file or uploader", allPurposes: "All purposes", refresh: "Refresh", file: "File", purposeLabel: "Purpose", size: "Size", uploader: "Uploader", created: "Uploaded", reference: "References", actions: "Actions", download: "Open file", delete: "Move to recycle bin", restore: "Restore", permanent: "Delete permanently", removeReferences: "Remove file references before permanent deletion", inUse: "In use ({count})", unused: "Not in use", loading: "Loading files...", empty: "No files found.", deleted: "File deleted.", restored: "File restored.", loadError: "Unable to load files.", actionError: "Unable to update file.", confirmTrash: "Move this file to the recycle bin?", confirmPermanent: "Permanently delete this file from storage? This cannot be undone.", selected: "{count} selected", select: "Select", selectAll: "Select all visible files", bulkTrash: "Move selected to recycle bin", bulkRestore: "Restore selected", bulkPermanent: "Permanently delete selected", confirmBulkTrash: "Move {count} selected files to the recycle bin? Files in use will be skipped.", confirmBulkPermanent: "Permanently delete {count} selected files? Files in use will be skipped.", bulkSummary: "{completed} completed, {skipped} skipped.",
  purpose: { attachment: "Attachment", company_logo: "Company logo", client_logo: "Client logo", font: "Font", pdf_export: "PDF export", excel_export: "Excel export" },
};

const zh: typeof en = {
  force: "强制删除", forceWarning: "永久删除这些文件，并解除下列引用。报价、公司和账号本身会保留。此操作无法撤销。", acknowledge: "我确认永久删除这些文件并清除相关引用。", cancel: "取消", activeUse: "正在使用", archival: "历史引用", pending: "删除待完成", retry: "重试删除", failed: "有 {count} 个文件删除未完成，可在回收站重试。", stale: "文件引用已发生变化，请重新查看并确认。",
  title: "附件", description: "统一管理上传资源和导出文件。正在使用的文件解除引用后才能最终删除。", upload: "上传附件", uploaded: "附件已上传。", uploadError: "无法上传附件。", active: "附件", trash: "回收站", search: "搜索文件或上传人", allPurposes: "全部类型", refresh: "刷新", file: "文件", purposeLabel: "类型", size: "大小", uploader: "上传人", created: "上传日期", reference: "引用状态", actions: "操作", download: "打开文件", delete: "移入回收站", restore: "恢复", permanent: "最终删除", removeReferences: "请先解除文件引用再最终删除", inUse: "使用中（{count}）", unused: "未使用", loading: "正在加载附件...", empty: "没有符合条件的文件。", deleted: "文件已删除。", restored: "文件已恢复。", loadError: "无法加载附件。", actionError: "无法更新附件。", confirmTrash: "将此文件移入回收站？", confirmPermanent: "从存储中最终删除此文件？此操作无法撤销。", selected: "已选择 {count} 项", select: "选择", selectAll: "选择当前全部文件", bulkTrash: "批量移入回收站", bulkRestore: "批量恢复", bulkPermanent: "批量最终删除", confirmBulkTrash: "将选中的 {count} 个文件移入回收站？使用中的文件会跳过。", confirmBulkPermanent: "最终删除选中的 {count} 个文件？使用中的文件会跳过。", bulkSummary: "已完成 {completed} 项，跳过 {skipped} 项。",
  purpose: { attachment: "附件", company_logo: "公司 Logo", client_logo: "客户 Logo", font: "字体", pdf_export: "PDF 导出", excel_export: "Excel 导出" },
};
